export type AgentEventType =
  | "answer_submitted_correct"
  | "answer_submitted_incorrect"
  | "word_hovered"
  | "activity_completed"
  | "stall"
  | "manual_help_requested";

import type { AgentAction } from "@lib/agent/actionToken";
import type { AgentLessonContent } from "@lib/agent/lessonSnapshot";

export type LessonPhase = "vocabulary" | "grammar" | "reading" | "speaking" | "quiz";

/** Serializable lesson payload for agent context (matches Prisma Lesson + optional unit/chapter). */
export type AgentLesson = {
  id: number;
  unitId: number;
  slug: string;
  name: string;
  titles: unknown;
  lessonType: string;
  orderIndex: number;
  createdAt?: string | Date;
  unit?: {
    id: number;
    chapterId: number;
    slug: string;
    name: string;
    titles: unknown;
    orderIndex?: number;
    createdAt?: string | Date;
    chapter?: {
      id: number;
      subjectId: number;
      slug: string;
      name: string;
      titles: unknown;
      orderIndex?: number;
      createdAt?: string | Date;
    };
  };
};

export function lessonTypeToPhase(lessonType: string): LessonPhase {
  if (lessonType === "SPEAKING") return "speaking";
  if (lessonType === "READING") return "reading";
  return "grammar";
}

export type AgentEvent = {
  type: AgentEventType;
  payload: string;
  relativeMs: number;
};

/** Client-side lesson binding before runtime fields are added. */
export type LessonContextInput = {
  lesson: AgentLesson;
  content?: AgentLessonContent | null;
  phase?: LessonPhase;
  targetLocale?: string;
  nativeLocale?: string;
};

export type LessonContext = {
  lesson: AgentLesson;
  content: AgentLessonContent | null;
  lessonId: string;
  lessonName: string;
  phase: LessonPhase;
  recentEvents: AgentEvent[];
  performance: { correct: number; incorrect: number; streak: number };
  targetLocale?: string;
  nativeLocale?: string;
};

export type ConversationDifficulty = "easy" | "standard" | "stretch";

export type ConversationPersona = {
  name: string;
  setting: string;
  voiceStyle: string;
  openingLine: string;
  scenarioId: string;
};

export type ConversationTranscriptRow = {
  role: "learner" | "persona";
  text: string;
  timestamp: string;
};

export type ConversationDebriefToReviewItem = {
  text: string;
  learnerText?: string;
  correction?: string;
  errorCategorySlug: string;
  unitSlug?: string;
  lessonId?: number;
  lessonHref?: string;
};

export type ConversationDebriefNewWordItem = {
  word: string;
  definition: string;
  wordId?: number;
};

export type ConversationDebrief = {
  usedWell: Array<{ text: string; example?: string }>;
  toReview: ConversationDebriefToReviewItem[];
  newWords: ConversationDebriefNewWordItem[];
  fluencyNote: string;
};

export type OverlayMode =
  | { type: "tutor" }
  | {
      type: "conversation";
      subphase: "picker" | "active";
      persona?: ConversationPersona;
      scenarioTitle: string;
      turnCount: number;
      sessionId?: number;
      difficulty: ConversationDifficulty;
      voiceFallback: boolean;
    }
  | {
      type: "debrief";
      sessionId: number;
      analyzing: boolean;
      debrief?: ConversationDebrief;
      transcriptCollapsed: boolean;
      error?: string;
    };

export type OverlayMessage = {
  id: string;
  text: string;
  isStreaming: boolean;
  role: "user" | "tutor" | "persona";
  /** Board action proposed by the tutor via a trailing [ACTION{...}] token (D-13). */
  action?: AgentAction;
};

export type OverlayState = {
  mode: OverlayMode;
  messages: OverlayMessage[];
  isOpen: boolean;
  /** Monotonic timestamp bumped when the collapsed widget should spring-spin (stall nudge). */
  nudgeAt: number;
};
